import Typography from "@mui/material/Typography";
import { ReportFilters } from "../../../../../domain/models/report-filters";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ReportFiltersComponent from "../../ReportFiltersComponent";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import { useEffect, useState } from "react";
import ReportScreen from "../../../../../common/enums/ReportScreens";
import ReportFiltersLoading from "../../ReportFiltersLoading";
import { TourProgramApprovalStore } from "../../../../../store/main/TourProgramApprovalStore";
import moment from "moment";
import {
  FetchTourProgramApprovalType,
  TourApproval,
  TourPagination,
} from "../../../../../domain/models/tour-program-approval/fetch-tour-program-approval";
import TourApprovalConfirmationModal from "../Modal/TourApprovalConfirmationModal";
import Swal from "sweetalert2";
import Status from "../../../../../common/enums/Status";

const commonReturnTableStyle = {
  border: "1px solid #e0e0e0",
  py: 0.1,
};

type FetchTourProgramApprovalParams = {
  month: string;
  year: number;
  q: string;
  status?: string;
  role_id?: number | undefined;
  zone_ids?: number[];
  region_ids?: number[];
  area_ids?: number[];
  headquarter_ids?: number[];
  page: number;
};

const TourProgramApprovalTab = () => {
  const { userDetails, decodeToken, userRole } = CustomerDatabaseStore();
  const {
    fetchTourProgramApproval,
    FetchTourProgramApprovalState,
    FetchTourProgramApproval,
    AddTourProgramApprovalState,
    addTourProgramApproval,
  } = TourProgramApprovalStore();

  const currentDate = new Date();
  const defaultMonth = currentDate.getMonth() + 1;
  const defaultYear = currentDate.getFullYear();
  const [month, setMonth] = useState<number>(defaultMonth);
  const [year, setYear] = useState<number>(defaultYear);
  const [loading, setLoading] = useState<boolean>(false);
  const [tourApprovalData, setTourApprovalData] = useState<TourApproval[]>([]);
  const [pagination, setPagination] = useState<TourPagination | null>(null);
  const [page, setPage] = useState<number>(1);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedTourId, setSelectedTourId] = useState<number | null>(null);
  const [forcedAction, setForcedAction] = useState<
    Status.APPROVE | Status.REJECT | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState<null | Status.APPROVE | Status.REJECT>(null);

  const defaultFilters = {
    month: defaultMonth.toString().padStart(2, "0"),
    year: defaultYear,
    q: "",
    status: Status.PENDING,
    role_id: undefined, 
    zone_ids: [],
    region_ids: [],
    area_ids: [],
    headquarter_ids: [],
    page: 1,
  };

  const [currentFilters, setCurrentFilters] =
    useState<FetchTourProgramApprovalParams>(defaultFilters);

  const handleSelectRow = (id: number) => {
    const tour = tourApprovalData.find((t) => t.id === id);
    if (tour?.status === Status.ACTIVE || tour?.status === Status.IN_ACTIVE)
      return;

    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };


  const isSelected = (id: number) => selectedRows.includes(id);
  const handleSingleApprove = (tourId: number) => {
    setSelectedTourId(tourId);
    setForcedAction(Status.APPROVE);
    setModalOpen(true);
  };

  const handleSingleReject = (tourId: number) => {
    setSelectedTourId(tourId);
    setForcedAction(Status.REJECT);
    setModalOpen(true);
  };

  const handleBulkAction = () => {
    setSelectedTourId(null);
    setForcedAction(undefined);
    setModalOpen(true);
  };

  const handleConfirmAction = async (
    status: Status.ACTIVE | Status.IN_ACTIVE
  ) => {
    setIsSubmitting(status === Status.ACTIVE ? Status.APPROVE : Status.REJECT);

    const payload = {
      tour_ids: selectedTourId !== null ? [selectedTourId] : selectedRows,
      status,
    };

    try {
      const response = await addTourProgramApproval(payload);
      if (response?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response?.message || "Action performed successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        setModalOpen(false);
        setSelectedRows([]);
        setSelectedTourId(null);
        setTourApprovalData((prev) =>
          prev.filter((t) => !payload.tour_ids.includes(t.id))
        );
        fetchTourApprovalData({ ...currentFilters, page: 1 });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text:
            response?.errors?.message ||
            response?.message ||
            "Something went wrong",
        });
      }
    } catch (err: any) {
      console.error("API error:", err);
      const errorMessage =
        err?.response?.data?.errors?.message || 
        err?.response?.data?.message || 
        err?.message ||
        "Something went wrong. Please try again.";

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    } finally {
      setIsSubmitting(null);
    }
  };

  useEffect(() => {
    decodeToken();
    fetchTourApprovalData({ ...currentFilters, page: 1 });
  }, []);

  const fetchTourApprovalData = async (params: {
    month: string;
    year: number;
    q?: string;
    status?: string;
    role_id?: number | null;
    zone_ids?: number[];
    region_ids?: number[];
    area_ids?: number[];
    headquarter_ids?: number[];
    page?: number;
  }) => {
    setLoading(true);
    try {
      const response = await fetchTourProgramApproval(params);
      if (response.success) {
        setTourApprovalData(response.tours);
        setPagination(response.paginated);
      } else {
        console.error("API returned failure:", response);
      }
    } catch (err) {
      console.error("Failed to fetch tour approval data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterData = async (filter: ReportFilters) => {
    const month = parseInt(moment(filter.date?.toString()).format("MM"));
    const year = parseInt(moment(filter.date?.toString()).format("YYYY"));
    setMonth(month);
    setYear(year);
    const monthString = month.toString().padStart(2, "0");
    const params = {
      ...defaultFilters, // keep structure consistent
      month: monthString,
      year: year,
      q: filter.search?.toString() ?? "",
      status: filter.status?.toString() ?? Status.PENDING,
      role_id: filter.role ? Number(filter.role) : undefined,
      zone_ids: Array.isArray(filter.zone) ? filter.zone.map(Number) : [],
      region_ids: Array.isArray(filter.region) ? filter.region.map(Number) : [],
      area_ids: Array.isArray(filter.area) ? filter.area.map(Number) : [],
      headquarter_ids: Array.isArray(filter.headquarter)
        ? filter.headquarter.map(Number)
        : [],
      page: page,
    };
    setCurrentFilters(params);
    setPage(1);
    fetchTourApprovalData({ ...params, page: 1 });
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value); // update page state

    fetchTourApprovalData({
      ...currentFilters, // 👈 keep the selected filters
      page: value, // 👈 only override the page
    });
  };

  const clearData = async () => {
    setMonth(defaultMonth);
    setYear(defaultYear);
    setCurrentFilters(defaultFilters);
    setPage(1);
    fetchTourApprovalData({ ...defaultFilters, page: 1 });
  };

  return (
    <>
      <Box pl={2} pt={1}>
        <Box mt={2}>
          {Object.keys(userDetails).length > 0 ? (
            <ReportFiltersComponent
              type={ReportScreen.TOUR_PROGRAM_APPROVAL}
              successCallback={filterData}
              clearSuccessCallBack={clearData}
              userRole={userRole.role}
              userDetails={userDetails}
              gridColumns={24}
              territoryMultiSelect={false}
            />
          ) : (
            <ReportFiltersLoading />
          )}
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : tourApprovalData.length === 0 ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="80vh"
          textAlign="center"
        >
          <Typography variant="h3" color="text.secondary">
            Approval Data is not available for this month.
          </Typography>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2} pr={5}>
            <Button
              variant="contained"
              disabled={selectedRows.length <= 1} // only enable when multiple rows selected
              onClick={handleBulkAction}
            >
              Bulk Action
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              ml: "auto",
              mr: "auto",
              width: "100%",
              maxWidth: "95%",
              overflowX: "auto", // horizontal scroll on small screens
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            <Table
              stickyHeader
              aria-label="input-tour-table"
              sx={{ minWidth: 650 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...commonReturnTableStyle }}>
                    <Checkbox
                      checked={
                        selectedRows.length ===
                          tourApprovalData.filter(
                            (t) =>
                              t.status !== Status.ACTIVE &&
                              t.status !== Status.IN_ACTIVE
                          ).length &&
                        tourApprovalData.some(
                          (t) =>
                            t.status !== Status.ACTIVE &&
                            t.status !== Status.IN_ACTIVE
                        )
                      }
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length <
                          tourApprovalData.filter(
                            (t) =>
                              t.status !== Status.ACTIVE &&
                              t.status !== Status.IN_ACTIVE
                          ).length
                      }
                      onChange={(e) =>
                        setSelectedRows(
                          e.target.checked
                            ? tourApprovalData
                                .filter(
                                  (t) =>
                                    t.status !== Status.ACTIVE &&
                                    t.status !== Status.IN_ACTIVE
                                )
                                .map((t) => t.id)
                            : []
                        )
                      }
                    />
                  </TableCell>

                  {[
                    "Name",
                    "Designation",
                    "Type",
                    "Brick",
                    "HQ",
                    "Date",
                    "Status",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        border: "1px solid #ccc",
                        backgroundColor: "#0D71B7",
                        color: "#fff",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        py: 1,
                        minWidth: { xs: 80, sm: 100 }, // smaller widths on mobile
                        display: {
                          xs:
                            header === "Type" || header === "HQ"
                              ? "none"
                              : "table-cell",
                          sm: "table-cell",
                        },
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {tourApprovalData.map((tour) => (
                  <TableRow key={tour.id} selected={isSelected(tour.id)}>
                    <TableCell sx={commonReturnTableStyle}>
                      <Checkbox
                        checked={isSelected(tour.id)}
                        onChange={() => handleSelectRow(tour.id)}
                      />
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.user ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.user_role ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...commonReturnTableStyle,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {tour.type ?? "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...commonReturnTableStyle,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {tour.hq_brick?.name ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.hq_headquarter?.name ?? "—"}
                    </TableCell>
                    <TableCell sx={commonReturnTableStyle}>
                      {tour.date ?? "—"}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={commonReturnTableStyle} align="center">
                      <Box
                        display="flex"
                        justifyContent="center"
                        gap={0.5}
                        flexWrap="wrap"
                      >
                        {tour.status === Status.ACTIVE ? (
                          <Typography
                            color="success.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            Approved
                          </Typography>
                        ) : tour.status === Status.IN_ACTIVE ? (
                          <Typography
                            color="error.main"
                            sx={{ fontWeight: "bold" }}
                          >
                            Rejected
                          </Typography>
                        ) : (
                          <>
                            <Button
                              variant="outlined"
                              size="small"
                              color="success"
                              sx={{
                                minWidth: { xs: 50, sm: 60 },
                                py: 0.3,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                              }}
                              disabled={
                                selectedRows.length === 0 ||
                                selectedRows.length > 1 ||
                                !isSelected(tour.id)
                              }
                              onClick={() => handleSingleApprove(tour.id)}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              sx={{
                                minWidth: { xs: 50, sm: 60 },
                                py: 0.3,
                                fontSize: { xs: "0.6rem", sm: "0.7rem" },
                              }}
                              disabled={
                                selectedRows.length === 0 ||
                                selectedRows.length > 1 ||
                                !isSelected(tour.id)
                              }
                              onClick={() => handleSingleReject(tour.id)}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {pagination && tourApprovalData.length > 0 &&(
        <Box display="flex" justifyContent="center" mt={2} mb={2}>
          <Pagination
            count={Math.ceil(pagination.total / pagination.per_page)}
            page={page}
            variant="outlined"
            color="primary"
            onChange={handlePageChange}
          />
        </Box>
      )}

      <TourApprovalConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmAction}
        selectedCount={
          selectedTourId === null ? selectedRows.length : undefined
        }
        title={
          selectedTourId === null
            ? "Bulk Action on Selected Tours"
            : forcedAction === Status.APPROVE
            ? "Approve Tour Program"
            : "Reject Tour Program"
        }
        message={
          selectedTourId !== null
            ? "Are you sure you want to perform this action?"
            : undefined
        }
        forcedAction={forcedAction}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default TourProgramApprovalTab;
